"use client";

import { useState, useRef } from "react";
import type {
  KalshiBookSide,
  KalshiConnectionState,
  KalshiEnvironment,
  KalshiMarketSearchResult,
  KalshiOrderPreview,
  KalshiOrderSummary,
  KalshiTimeInForce,
} from "@/lib/kalshi";
import { usePreviewExpiry } from "./use-preview-expiry";
import { useDialogFocus } from "./use-dialog-focus";
import { useConfirmation } from "./use-confirmation";
import { apiFetch } from "@/lib/client-api";

type KalshiTradeTicketProps = {
  environment: KalshiEnvironment;
  connection: KalshiConnectionState;
  onCompleted: () => Promise<void>;
};

type OrderDraft = {
  ticker: string;
  side: KalshiBookSide;
  count: string;
  price: string;
  timeInForce: KalshiTimeInForce;
  postOnly: boolean;
  reduceOnly: boolean;
};

type PreviewIntent = {
  id: string;
  environment: KalshiEnvironment;
  kind: "create_order" | "cancel_order";
  state: string;
  expiresAt: string;
  preview: KalshiOrderPreview | KalshiOrderSummary;
  confirmationText: string;
};

function blankOrder(): OrderDraft {
  return {
    ticker: "",
    side: "bid",
    count: "1.00",
    price: "0.5000",
    timeInForce: "good_till_canceled",
    postOnly: false,
    reduceOnly: false,
  };
}

function money(value: number | null, digits = 2) {
  if (value === null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function number(value: number | null) {
  return value === null ? "—" : new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
}

async function postOrderAction(body: unknown) {
  const response = await apiFetch("/api/kalshi/orders", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as {
    intent?: PreviewIntent;
    result?: Record<string, unknown>;
    state?: string;
    error?: string;
    code?: string;
  };
  if (!response.ok) {
    const error = new Error(payload.error ?? "The Kalshi request failed.");
    Object.assign(error, { code: payload.code, state: payload.state });
    throw error;
  }
  return payload;
}

export default function KalshiTradeTicket({
  environment,
  connection,
  onCompleted,
}: KalshiTradeTicketProps) {
  const [open, setOpen] = useState(false);
  const ticketRef = useRef<HTMLDivElement>(null);
  const { confirm, confirmationDialog } = useConfirmation();
  const [draft, setDraft] = useState<OrderDraft>(blankOrder);
  const [previewIntent, setPreviewIntent] = useState<PreviewIntent | null>(null);
  const previewExpired = usePreviewExpiry(previewIntent?.expiresAt);
  const [confirmation, setConfirmation] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [marketResults, setMarketResults] = useState<KalshiMarketSearchResult[]>([]);
  const [searchingMarkets, setSearchingMarkets] = useState(false);
  const label = environment === "demo" ? "Paper" : "Real";
  const settingsUrl = environment === "production"
    ? "https://kalshi.com/account/profile"
    : null;

  useDialogFocus(open, ticketRef, () => { if (!busy) closeTicket(); });

  function setField<Key extends keyof OrderDraft>(key: Key, value: OrderDraft[Key]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setPreviewIntent(null);
    setConfirmation("");
    setAuthorized(false);
    setResult(null);
    if (key === "ticker") setMarketResults([]);
  }

  function closeTicket() {
    setOpen(false);
    setDraft(blankOrder());
    setPreviewIntent(null);
    setConfirmation("");
    setAuthorized(false);
    setError(null);
    setResult(null);
    setMarketResults([]);
  }

  async function searchMarkets() {
    const query = draft.ticker.trim();
    if (query.length < 2) {
      setError("Enter at least two letters or a Kalshi ticker to search.");
      return;
    }
    setSearchingMarkets(true);
    setError(null);
    try {
      const response = await apiFetch(
        `/api/kalshi/markets?environment=${environment}&q=${encodeURIComponent(query)}`,
        { cache: "no-store", headers: { accept: "application/json" } },
      );
      const payload = await response.json() as {
        markets?: KalshiMarketSearchResult[];
        error?: string;
      };
      if (!response.ok || !payload.markets) {
        throw new Error(payload.error ?? "Kalshi market search failed.");
      }
      setMarketResults(payload.markets);
      if (!payload.markets.length) setError(`No open Kalshi markets matched “${query}”.`);
    } catch (searchError) {
      setError(searchError instanceof Error ? searchError.message : "Kalshi market search failed.");
    } finally {
      setSearchingMarkets(false);
    }
  }

  function selectMarket(market: KalshiMarketSearchResult) {
    const referencePrice = market.yesAsk ?? market.lastPrice ?? market.yesBid;
    setDraft((current) => ({
      ...current,
      ticker: market.ticker,
      price: referencePrice === null ? current.price : referencePrice.toFixed(4),
    }));
    setMarketResults([]);
    setPreviewIntent(null);
    setConfirmation("");
    setAuthorized(false);
    setResult(null);
  }

  async function previewOrder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const payload = await postOrderAction({
        action: "preview_create",
        order: { environment, ...draft },
      });
      if (!payload.intent) throw new Error("Kalshi did not return an order preview.");
      setPreviewIntent(payload.intent);
      setConfirmation("");
      setAuthorized(false);
    } catch (previewError) {
      setError(previewError instanceof Error ? previewError.message : "The order could not be previewed.");
    } finally {
      setBusy(false);
    }
  }

  async function executeOrder() {
    if (!previewIntent || previewExpired || !connection.ordersEnabled || !authorized || confirmation !== previewIntent.confirmationText) return;
    const preview = previewIntent.preview as KalshiOrderPreview;
    if (!await confirm(
      `${previewIntent.confirmationText}\n\n${preview.ticker} · ${preview.sideLabel}\n${preview.count} contracts at YES $${preview.yesPrice}\nMaximum capital at risk ${money(preview.maximumCapitalAtRisk)}\n\nSubmit this exact order?`,
    )) return;
    if (Date.parse(previewIntent.expiresAt) <= Date.now()) { setError("This preview expired. Preview the order again."); return; }
    setBusy(true);
    setError(null);
    try {
      const payload = await postOrderAction({
        action: "execute",
        intentId: previewIntent.id,
        confirmation,
      });
      const orderId = typeof payload.result?.order_id === "string" ? payload.result.order_id : "recorded";
      const filled = typeof payload.result?.fill_count === "string" ? payload.result.fill_count : null;
      const remaining = typeof payload.result?.remaining_count === "string" ? payload.result.remaining_count : null;
      setResult(`Order ${orderId}${filled ? ` · ${filled} filled` : ""}${remaining ? ` · ${remaining} resting` : ""}`);
      setPreviewIntent(null);
      setConfirmation("");
      setAuthorized(false);
      await onCompleted();
    } catch (executeError) {
      setError(executeError instanceof Error ? executeError.message : "The order result is unavailable.");
    } finally {
      setBusy(false);
    }
  }

  async function cancelOrder(order: KalshiOrderSummary) {
    if (!await confirm(
      `Prepare cancellation for ${order.ticker} order ${order.orderId}?\n\nThis will require one final confirmation before Kalshi receives it.`,
    )) return;
    setCancelingId(order.orderId);
    setError(null);
    setResult(null);
    try {
      const previewPayload = await postOrderAction({
        action: "preview_cancel",
        environment,
        orderId: order.orderId,
      });
      if (!previewPayload.intent) throw new Error("Kalshi did not return a cancellation preview.");
      if (!await confirm(
        `${previewPayload.intent.confirmationText}\n\n${order.ticker} · ${number(order.remainingCount)} contracts remaining\n\nCancel this resting order now?`,
      )) return;
      const payload = await postOrderAction({
        action: "execute",
        intentId: previewPayload.intent.id,
        confirmation: previewPayload.intent.confirmationText,
      });
      const reduced = typeof payload.result?.reduced_by === "string" ? payload.result.reduced_by : "remaining";
      setResult(`Canceled ${reduced} contracts from ${order.orderId}.`);
      await onCompleted();
    } catch (cancelError) {
      setError(cancelError instanceof Error ? cancelError.message : "The cancellation result is unavailable.");
    } finally {
      setCancelingId(null);
    }
  }

  const preview = previewIntent?.kind === "create_order"
    ? previewIntent.preview as KalshiOrderPreview
    : null;

  return (
    <section className="kalshi-console" aria-label={`Kalshi ${label} trading`}>
      <header>
        <div>
          <span>Kalshi · {label}</span>
          <strong>{connection.status === "live" ? connection.message : `${label} account not connected`}</strong>
        </div>
        {connection.ordersEnabled ? (
          <button type="button" onClick={() => open ? closeTicket() : setOpen(true)}>
            {open ? "Close ticket" : "New order"}
          </button>
        ) : settingsUrl ? (
          <a href={settingsUrl} target="_blank" rel="noreferrer">
            {connection.status === "live" ? "Enable write key" : "Create API key"}
          </a>
        ) : (
          <button type="button" disabled>Paper unavailable</button>
        )}
      </header>

      <details className="connection-details"><summary>Connection details</summary><p>{connection.message}</p><dl className="kalshi-account-line">
        <div><dt>Available</dt><dd>{money(connection.availableBalance)}</dd></div>
        <div><dt>Portfolio</dt><dd>{money(connection.portfolioValue)}</dd></div>
        <div><dt>Resting</dt><dd>{connection.openOrderCount}</dd></div>
        <div><dt>Per-order cap</dt><dd>{money(connection.maximumOrderUsd, 0)}</dd></div>
      </dl></details>

      {open && connection.ordersEnabled && (
        <div className="ticket-overlay"><button type="button" className="backdrop-dismiss" aria-label="Close Kalshi order" disabled={busy} onClick={closeTicket}/><div ref={ticketRef} role="dialog" aria-modal="true" aria-label={`Kalshi ${label.toLowerCase()} order`} className="ticket-dialog"><header className="ticket-dialog-header"><div><h2>Kalshi</h2><span>{label} order</span></div><button type="button" disabled={busy} onClick={closeTicket}>Close</button></header><form className="kalshi-ticket" onSubmit={(event) => void previewOrder(event)}>
          <div className="kalshi-ticket-grid">
            <label>
              <span>Market ticker</span>
              <div className="kalshi-ticker-input">
                <input
                  required
                  maxLength={120}
                  value={draft.ticker}
                  onChange={(event) => setField("ticker", event.target.value.toUpperCase())}
                  placeholder="Ticker or topic"
                />
                <button type="button" onClick={() => void searchMarkets()} disabled={searchingMarkets}>
                  {searchingMarkets ? "Searching" : "Find"}
                </button>
              </div>
            </label>
            <label>
              <span>Book side</span>
              <select value={draft.side} onChange={(event) => setField("side", event.target.value as KalshiBookSide)}>
                <option value="bid">{environment === "demo" ? "Buy YES" : "Bid · buy YES / sell NO"}</option>
                <option value="ask">{environment === "demo" ? "Buy NO" : "Ask · sell YES / buy NO"}</option>
              </select>
            </label>
            <label>
              <span>YES-side limit</span>
              <input
                required
                min="0.0001"
                max="0.9999"
                step="0.0001"
                type="number"
                value={draft.price}
                onChange={(event) => setField("price", event.target.value)}
              />
            </label>
            <label>
              <span>Contracts</span>
              <input
                required
                min="0.01"
                step="0.01"
                type="number"
                value={draft.count}
                onChange={(event) => setField("count", event.target.value)}
              />
            </label>
            <label>
              <span>Time in force</span>
              <select
                value={draft.timeInForce}
                onChange={(event) => setField("timeInForce", event.target.value as KalshiTimeInForce)}
              >
                <option value="good_till_canceled">Good till canceled</option>
                <option value="immediate_or_cancel">Immediate or cancel</option>
                <option value="fill_or_kill">Fill or kill</option>
              </select>
            </label>
          </div>

          {marketResults.length > 0 && (
            <div className="kalshi-market-results" role="list" aria-label="Open Kalshi market matches">
              {marketResults.map((market) => (
                <button key={market.ticker} type="button" onClick={() => selectMarket(market)}>
                  <span>{market.ticker}</span>
                  <strong>{market.title}</strong>
                  <small>YES ask {money(market.yesAsk, 4)}</small>
                </button>
              ))}
            </div>
          )}

          <details className="kalshi-advanced">
            <summary>Execution controls</summary>
            <label>
              <input
                type="checkbox"
                checked={draft.postOnly}
                disabled={draft.timeInForce !== "good_till_canceled"}
                onChange={(event) => setField("postOnly", event.target.checked)}
              />
              Post only
            </label>
            <label>
              <input
                type="checkbox"
                checked={draft.reduceOnly}
                disabled={environment === "demo"}
                onChange={(event) => setField("reduceOnly", event.target.checked)}
              />
              Reduce only
            </label>
            <span>{environment === "demo"
              ? "Paper fills use observed top-of-book prices; fees, queue position, and partial liquidity are not modeled."
              : "Self-trade prevention and cancel-on-pause are always on."}</span>
          </details>

          {!preview && (
            <footer>
              <span>No order is sent while previewing.</span>
              <button type="submit" disabled={busy}>{busy ? "Checking" : `Review ${label.toLowerCase()} order`}</button>
            </footer>
          )}

          {preview && (
            <div className="kalshi-preview">
              <header>
                <div>
                  <span>Single-use preview · expires in five minutes</span>
                  <strong>{preview.title}</strong>
                </div>
                <button type="button" onClick={() => setPreviewIntent(null)}>Revise</button>
              </header>
              <dl>
                <div><dt>Instruction</dt><dd>{preview.sideLabel}</dd></div>
                <div><dt>YES limit</dt><dd>${preview.yesPrice}</dd></div>
                <div><dt>NO equivalent</dt><dd>${preview.equivalentNoPrice}</dd></div>
                <div><dt>Contracts</dt><dd>{preview.count}</dd></div>
                <div><dt>Capital at risk</dt><dd>{money(preview.maximumCapitalAtRisk)}</dd></div>
                <div><dt>Max payout</dt><dd>{money(preview.maximumPayout)}</dd></div>
                <div><dt>Best YES bid</dt><dd>{money(preview.bestYesBid, 4)}</dd></div>
                <div><dt>Best YES ask</dt><dd>{money(preview.bestYesAsk, 4)}</dd></div>
              </dl>
              <p>
                {preview.likelyMarketable === null
                  ? "No complete top-of-book comparison is available."
                  : preview.likelyMarketable
                    ? "This limit appears marketable and may fill immediately."
                    : "This limit is not currently marketable and may rest."}
                {" "}Fees are not included in the estimate.
              </p>
              {previewExpired && <p role="status">This preview expired. Preview the order again.</p>}
              <label className="kalshi-confirmation">
                <span>Type {previewIntent!.confirmationText}</span>
                <input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="kalshi-authorize">
                <input
                  type="checkbox"
                  checked={authorized}
                  onChange={(event) => setAuthorized(event.target.checked)}
                />
                I authorize this exact {label.toLowerCase()} order. No agent may submit it for me.
              </label>
              <footer>
                <span>One submission · no automatic retry</span>
                <button
                  type="button"
                  disabled={busy || previewExpired || !connection.ordersEnabled || !authorized || confirmation !== previewIntent!.confirmationText}
                  onClick={() => void executeOrder()}
                >
                  {busy ? "Submitting once" : `Place ${label.toLowerCase()} order`}
                </button>
              </footer>
            </div>
          )}
        </form>{error && <p className="kalshi-message error" role="alert">{error}</p>}</div></div>
      )}

      {connection.openOrders.length > 0 && (
        <details className="kalshi-open-orders">
          <summary>{connection.openOrders.length} resting Kalshi orders</summary>
          {connection.openOrders.map((order) => (
            <div key={order.orderId}>
              <span>{order.ticker}</span>
              <span>{order.bookSide} · YES {money(order.yesPrice, 4)}</span>
              <span>{number(order.remainingCount)} remaining</span>
              <button
                type="button"
                disabled={cancelingId !== null || !connection.ordersEnabled}
                onClick={() => void cancelOrder(order)}
              >
                {cancelingId === order.orderId ? "Confirming" : "Cancel"}
              </button>
            </div>
          ))}
        </details>
      )}

      {!open && (error || result) && (
        <p className={error ? "kalshi-message error" : "kalshi-message"} role={error ? "alert" : "status"}>
          {error ?? result}
        </p>
      )}
      {confirmationDialog}
    </section>
  );
}
